import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import useGlobals from './useGlobals';
import {
    setBasePrompt as setBasePromptSetting,
    setListReloadType,
    setSelectedPrompt,
} from '../Store/Actions/environmentActions';
import { setModal } from '../Store/Actions/modalActions';
import { ModalData, Prompt } from '../Types';
import { promptService } from '../Services/promptService';
import { PaginationListType } from '../Types/PaginationParams';

const usePrompts = () => {
    const { handleNavigate, userSettings, i18n, demo, dispatch, windowWidth, getBreakpoint } = useGlobals();

    const [prompts, setPrompts] = useState<Prompt[]>([]);
    const [basePromptIdentifier, setBasePromptIdentifier] = useState<string>();
    const [promptsLoaded, setPromptsLoaded] = useState<boolean>(false);

    const notifySuccess = useCallback(
        (type: 'proposeGlobal' | 'makeGlobal' | 'unproposeGlobalSuccess') => {
            switch (type) {
                case 'proposeGlobal':
                    toast.success(i18n.translate({ id: 'toast.proposeGlobalSuccess', sourceName: 'Prompts' }));
                    break;
                case 'makeGlobal':
                    toast.success(i18n.translate({ id: 'toast.makeGlobalSuccess', sourceName: 'Prompts' }));
                    break;
                case 'unproposeGlobalSuccess':
                    toast.success(i18n.translate({ id: 'toast.unproposeGlobalSuccess', sourceName: 'Prompts' }));
                    break;
            }
        },
        [i18n],
    );

    const handleBasePromptChange = useCallback(
        (identifier: string) => {
            setBasePromptIdentifier(identifier);
            if (demo) return;
            dispatch(setBasePromptSetting(prompts.find((prompt: Prompt) => prompt.isBasePrompt)));

            promptService.setBasePrompt(identifier).then((response) => {
                setPrompts(response.data.data);
                dispatch(setListReloadType(PaginationListType.Assistants));
            });
        },
        [demo, prompts, dispatch],
    );

    const handleProposeGlobal = useCallback(
        (identifier: string) => {
            if (demo) {
                notifySuccess('proposeGlobal');
                return;
            }
            if (!userSettings.isLoggedIn) return;
            promptService.proposeGlobal(identifier).then(() => {
                setPrompts(() => {
                    return prompts.map((prompt) => {
                        if (prompt.identifier === identifier) {
                            prompt.proposedGlobal = true;
                        }
                        return prompt;
                    });
                });
                notifySuccess('proposeGlobal');
            });
        },
        [demo, prompts, userSettings.isLoggedIn, notifySuccess],
    );

    const handleRemoveProposedGlobal = useCallback(
        (identifier: string) => {
            if (demo) {
                notifySuccess('unproposeGlobalSuccess');
                return;
            }
            if (!userSettings.isLoggedIn) return;

            promptService.unproposeGlobal(identifier).then(() => {
                setPrompts(() =>
                    prompts.filter((prompt) => !(prompt.identifier === identifier && prompt.proposedGlobal === true)),
                );
                notifySuccess('unproposeGlobalSuccess');
            });
        },
        [demo, prompts, userSettings.isLoggedIn, notifySuccess],
    );

    const handleMakeGlobal = useCallback(
        (identifier: string) => {
            if (demo) {
                notifySuccess('makeGlobal');
                return;
            }
            if (!userSettings.isLoggedIn) return;
            promptService.makeGlobal(identifier).then((response) => {
                if (response.data.data !== undefined) {
                    setPrompts(response.data.data);
                    dispatch(setListReloadType(PaginationListType.Assistants));
                    notifySuccess('makeGlobal');
                }
            });
        },
        [demo, userSettings.isLoggedIn, notifySuccess, dispatch],
    );

    const deletePrompt = useCallback(
        (identifier: string) => {
            if (demo) {
                return;
            }
            if (!userSettings.isLoggedIn) return;
            promptService.deletePrompt(identifier).then(() => {
                setPrompts(prompts.filter((prompt) => prompt.identifier !== identifier));
                dispatch(setListReloadType(PaginationListType.Assistants));
            });
        },
        [demo, userSettings.isLoggedIn, prompts, dispatch],
    );

    const handleOpenConfirmDialog = useCallback(
        (promptIdentifier: string) => {
            dispatch(
                setModal({
                    state: 'open',
                    title: i18n.translate({ id: 'promptDelete.title', sourceName: 'Prompts' }),
                    content: i18n.translate({ id: 'promptDelete.text', sourceName: 'Prompts' }),
                    modalSize: 'small',
                    closeOnAction: true,
                    showCloseButton: false,
                    buttonRightAction: () => deletePrompt(promptIdentifier),
                } as ModalData),
            );
        },
        [i18n, deletePrompt, dispatch],
    );

    const handleOpenConfirmDialogBasePromptChange = useCallback(
        (promptIdentifier: string) => {
            dispatch(
                setModal({
                    state: 'open',
                    title: i18n.translate({ id: 'basePromptChange.title', sourceName: 'Prompts' }),
                    content: i18n.translate({ id: 'basePromptChange.text', sourceName: 'Prompts' }),
                    modalSize: 'small',
                    closeOnAction: true,
                    showCloseButton: false,
                    buttonRightAction: () => handleBasePromptChange(promptIdentifier),
                } as ModalData),
            );
        },
        [i18n, handleBasePromptChange, dispatch],
    );

    const createNewChat = useCallback(
        async (promptIdentifier: string) => {
            dispatch(setSelectedPrompt(promptIdentifier));
            handleNavigate(`/start/${promptIdentifier}`);
        },
        [dispatch, handleNavigate],
    );

    const PromptsActions = useMemo(
        () => ({
            handleBasePromptChange,
            handleProposeGlobal,
            handleRemoveProposedGlobal,
            handleMakeGlobal,
            handleOpenConfirmDialog,
            handleOpenConfirmDialogBasePromptChange,
            createNewChat,
        }),
        [
            handleBasePromptChange,
            handleProposeGlobal,
            handleRemoveProposedGlobal,
            handleMakeGlobal,
            handleOpenConfirmDialog,
            handleOpenConfirmDialogBasePromptChange,
            createNewChat,
        ],
    );

    return {
        PromptsActions,
        userSettings,
        demo,
        i18n,
        handleNavigate,
        basePromptIdentifier,
        promptsLoaded,
        prompts,
        windowWidth,
        getBreakpoint,
    };
};

export default usePrompts;
